import React, { Component, Fragment } from "react";
import { Search, Copy, X, Save, Loader2 } from "lucide-react";
import { Row, Col, Label, FormGroup } from "reactstrap";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { toastService } from "../../services/toastService";
import { StructuredAddress, StructuredAddressRequest } from "../../models/StructuredAddress";
import { structuredAddressApiService } from "../../services/api/structuredAddress/StructuredAddressApiService";
import { SelectItem } from "../../models/SelectItem";
import { LoadingDots } from "../../components/ui";
import { utils } from "../../utils/utils";
import { isComplianceOfficer, isComplianceOfficerApprover, isSuperAdmin } from "../../authGuard";
import _ from "lodash";

interface StructuredAddressComponentProps {
    customerNo: string;
    toast?: any;
}

interface StructuredAddressComponentState {
    isLoading: boolean;
    isSaving: boolean;
    structuredAddress: StructuredAddress;
    originalAddress: StructuredAddress;
    showJsonModal: boolean;
    countries: SelectItem<string>[];
    hasExistingRecord: boolean;
}

export default class StructuredAddressComponent extends Component<
    StructuredAddressComponentProps,
    StructuredAddressComponentState
> {
    constructor(props: StructuredAddressComponentProps) {
        super(props);

        this.state = {
            isLoading: false,
            isSaving: false,
            structuredAddress: this.getEmptyStructuredAddress(),
            originalAddress: this.getEmptyStructuredAddress(),
            showJsonModal: false,
            countries: [],
            hasExistingRecord: false,
        };
    }

    canManageStructuredAddress = () => isSuperAdmin() || isComplianceOfficer() || isComplianceOfficerApprover();

    async componentDidMount() {
        if (!this.canManageStructuredAddress()) {
            return; // Component will not render for unauthorized roles
        }

        this.setState({ isLoading: true });

        // Load countries
        const countriesResult = await utils.getCountriesResult();
        if (countriesResult.success) {
            const countries = utils.getCountries().map(country => ({
                label: country.label,
                value: country.value // ISO-2 code
            }));
            this.setState({ countries });
        }

        // Load existing structured address
        await this.loadStructuredAddress();

        this.setState({ isLoading: false });
    }

    getEmptyStructuredAddress = (): StructuredAddress => ({
        customerNo: this.props.customerNo,
        department: "",
        subDepartment: "",
        streetName: "",
        buildingNumber: "",
        buildingName: "",
        floor: "",
        postBox: "",
        room: "",
        postCode: "",
        townName: "",
        townLocName: "",
        districtName: "",
        countrySubDiv: "",
        country: "",
    });

    loadStructuredAddress = async () => {
        const individualId = parseInt(this.props.customerNo);

        if (isNaN(individualId)) {
            console.error('Invalid customerNo - cannot convert to number:', this.props.customerNo);
            return;
        }

        const result = await structuredAddressApiService.getCustomerStructuredAddress(individualId);
        
        if (result.success && result.data) {
            
            const addressData = result.data.customerAddress;
            
            // Create a clean address object with only the fields we care about
            const cleanAddressData: StructuredAddress = {
                customerNo: this.props.customerNo,
                department: addressData.department || "",
                subDepartment: addressData.subDepartment || "",
                streetName: addressData.streetName || "",
                buildingNumber: addressData.buildingNumber || "",
                buildingName: addressData.buildingName || "",
                floor: addressData.floor || "",
                postBox: addressData.postBox || "",
                room: addressData.room || "",
                postCode: addressData.postCode || "",
                townName: addressData.townName || "",
                townLocName: addressData.townLocName || "",
                districtName: addressData.districtName || "",
                countrySubDiv: addressData.countrySubDiv || "",
                country: addressData.country || "",
            };

            this.setState({
                structuredAddress: cleanAddressData,
                originalAddress: _.cloneDeep(cleanAddressData),
                hasExistingRecord: result.data.existsInFlexCube === true,
            });
        } else if (!result.success && !result.message?.includes("Record Not Found")) {
            console.error("Failed to load structured address:", result.message);
            this.showToast("error", "Error", "Failed to load structured address");
        }
    };

    onFieldChange = (field: keyof StructuredAddress, value: string) => {
        this.setState(prevState => ({
            structuredAddress: {
                ...prevState.structuredAddress,
                [field]: value,
            },
        }));
    };

    validateForm = (): { isValid: boolean; errors: string[] } => {
        const { structuredAddress } = this.state;
        const errors: string[] = [];

        if (!structuredAddress.country || structuredAddress.country.trim() === "") {
            errors.push("Country is required.");
        }

        if (!structuredAddress.townName || structuredAddress.townName.trim() === "") {
            errors.push("City / Town is required.");
        }

        // Validate field lengths
        const lengthValidations = [
            { field: "department", max: 70 },
            { field: "subDepartment", max: 70 },
            { field: "streetName", max: 70 },
            { field: "buildingName", max: 70 },
            { field: "townName", max: 70 },
            { field: "townLocName", max: 70 },
            { field: "districtName", max: 70 },
            { field: "countrySubDiv", max: 70 },
            { field: "buildingNumber", max: 10 },
            { field: "floor", max: 10 },
            { field: "room", max: 10 },
            { field: "postBox", max: 12 },
            { field: "postCode", max: 16 },
        ];

        lengthValidations.forEach(({ field, max }) => {
            const value = structuredAddress[field as keyof StructuredAddress] as string;
            if (value && value.length > max) {
                errors.push(`${field} cannot exceed ${max} characters.`);
            }
        });

        return { isValid: errors.length === 0, errors };
    };

    getChangedFields = (): Partial<StructuredAddressRequest> => {
        const changed: Partial<StructuredAddressRequest> = { customerNo: this.props.customerNo };
        
        Object.keys(this.state.structuredAddress).forEach(key => {
            const field = key as keyof StructuredAddress;
            if (field !== 'customerNo') {
                const current = this.state.structuredAddress[field];
                const original = this.state.originalAddress[field];
                
                if (current !== original) {
                    (changed as any)[field] = current;
                }
            }
        });

        return changed;
    };

    onSave = async () => {
        const validation = this.validateForm();
        if (!validation.isValid) {
            validation.errors.forEach(error => {
                this.showToast("error", "Validation Error", error);
            });
            return;
        }

        this.setState({ isSaving: true });

        try {
            let result;
            const individualId = parseInt(this.props.customerNo);
            
            if (isNaN(individualId)) {
                console.error('Invalid customerNo - cannot convert to number:', this.props.customerNo);
                this.showToast("error", "Error", "Invalid customer ID");
                this.setState({ isSaving: false });
                return;
            }
            
            if (this.state.hasExistingRecord) {
                const changedFields = this.getChangedFields();
                
                if (Object.keys(changedFields).length <= 1) { // Only customerNo
                    this.showToast("info", "No Changes", "No changes detected.");
                    this.setState({ isSaving: false });
                    return;
                }
                
                const modifyRequestData: StructuredAddressRequest = {
                    customerNo: this.props.customerNo,
                    department: (changedFields.department ?? this.state.structuredAddress.department ?? "").trim() || undefined,
                    subDepartment: (changedFields.subDepartment ?? this.state.structuredAddress.subDepartment ?? "").trim() || undefined,
                    streetName: (changedFields.streetName ?? this.state.structuredAddress.streetName ?? "").trim() || undefined,
                    buildingNumber: (changedFields.buildingNumber ?? this.state.structuredAddress.buildingNumber ?? "").trim() || undefined,
                    buildingName: (changedFields.buildingName ?? this.state.structuredAddress.buildingName ?? "").trim() || undefined,
                    floor: (changedFields.floor ?? this.state.structuredAddress.floor ?? "").trim() || undefined,
                    postBox: (changedFields.postBox ?? this.state.structuredAddress.postBox ?? "").trim() || undefined,
                    room: (changedFields.room ?? this.state.structuredAddress.room ?? "").trim() || undefined,
                    postCode: (changedFields.postCode ?? this.state.structuredAddress.postCode ?? "").trim() || undefined,
                    townName: changedFields.townName ?? this.state.structuredAddress.townName,
                    townLocName: (changedFields.townLocName ?? this.state.structuredAddress.townLocName ?? "").trim() || undefined,
                    districtName: (changedFields.districtName ?? this.state.structuredAddress.districtName ?? "").trim() || undefined,
                    countrySubDiv: (changedFields.countrySubDiv ?? this.state.structuredAddress.countrySubDiv ?? "").trim() || undefined,
                    country: changedFields.country ?? this.state.structuredAddress.country,
                };
                
                result = await structuredAddressApiService.modifyCustomerStructuredAddress(individualId, modifyRequestData);
            } else {
                const requestData: StructuredAddressRequest = {
                    customerNo: this.props.customerNo,
                    department: (this.state.structuredAddress.department ?? "").trim() || undefined,
                    subDepartment: (this.state.structuredAddress.subDepartment ?? "").trim() || undefined,
                    streetName: (this.state.structuredAddress.streetName ?? "").trim() || undefined,
                    buildingNumber: (this.state.structuredAddress.buildingNumber ?? "").trim() || undefined,
                    buildingName: (this.state.structuredAddress.buildingName ?? "").trim() || undefined,
                    floor: (this.state.structuredAddress.floor ?? "").trim() || undefined,
                    postBox: (this.state.structuredAddress.postBox ?? "").trim() || undefined,
                    room: (this.state.structuredAddress.room ?? "").trim() || undefined,
                    postCode: (this.state.structuredAddress.postCode ?? "").trim() || undefined,
                    townName: this.state.structuredAddress.townName,
                    townLocName: (this.state.structuredAddress.townLocName ?? "").trim() || undefined,
                    districtName: (this.state.structuredAddress.districtName ?? "").trim() || undefined,
                    countrySubDiv: (this.state.structuredAddress.countrySubDiv ?? "").trim() || undefined,
                    country: this.state.structuredAddress.country,
                };
                
                result = await structuredAddressApiService.createCustomerStructuredAddress(individualId, requestData);
            }

            if (result.success) {
                this.showToast("success", "Success", "Structured address saved successfully.");
                this.setState({
                    hasExistingRecord: true,
                    originalAddress: _.cloneDeep(this.state.structuredAddress),
                });
            } else {
                 console.error("Structured address save failed:", result.message);
                this.showToast("error", "Error", "Error saving structured address.");
            }
        } catch (error) {
            this.showToast("error", "Error", "An unexpected error occurred.");
        }

        this.setState({ isSaving: false });
    };

    showToast = (severity: "success" | "info" | "warn" | "error", summary: string, detail: string) => {
        toastService[severity](detail, summary);
    };

    onShowJsonModal = () => {
        this.setState({ showJsonModal: true });
    };

    onHideJsonModal = () => {
        this.setState({ showJsonModal: false });
    };

    copyToClipboard = () => {
        // As per requirements, show JSON from KycSubmissions.ExternalIdentificationMetadata
        // For now, we'll show the current form data, but this should be updated to fetch from the correct source
        const json = JSON.stringify(this.state.structuredAddress, null, 2);
        navigator.clipboard.writeText(json).then(() => {
            this.showToast("success", "Copied", "JSON copied to clipboard.");
        });
    };

    renderJsonModal = () => {
        // Check if there's meaningful address data (exclude customerNo from the check)
        const addressFields = Object.keys(this.state.structuredAddress).filter(key => key !== 'customerNo');
        const hasAddressData = addressFields.some(key => {
            const value = this.state.structuredAddress[key as keyof StructuredAddress];
            return value && value.trim() !== "";
        });

        const displayContent = hasAddressData
            ? JSON.stringify(this.state.structuredAddress, null, 2)
            : "Structured Address not available";

        return (
            <Dialog
                header="Structured Address JSON"
                visible={this.state.showJsonModal}
                onHide={this.onHideJsonModal}
                style={{ width: '600px' }}
                footer={
                    <div>
                        <Button
                            label="Copy to Clipboard"
                            icon={<Copy size={16} />}
                            onClick={this.copyToClipboard}
                            outlined
                            className="mr-2"
                            disabled={!hasAddressData}
                        />
                        <Button
                            label="Close"
                            icon={<X size={16} />}
                            onClick={this.onHideJsonModal}
                            outlined
                        />
                    </div>
                }
            >
                <pre style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '400px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    border: '1px solid #dee2e6',
                    textAlign: hasAddressData ? 'left' : 'center',
                    color: hasAddressData ? 'inherit' : '#6c757d'
                }}>
                    {displayContent}
                </pre>
            </Dialog>
        );
    };

    render() {
        // Hide component for unauthorized roles
        if (!this.canManageStructuredAddress()) {
            return null;
        }

        if (this.state.isLoading) {
            return (
                <div style={{ minHeight: "200px", position: "relative" }}>
                    <LoadingDots />
                </div>
            );
        }

        const { structuredAddress } = this.state;

        return (
            <Fragment>
                <fieldset style={{ border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '1rem', margin: '1rem 0' }}>
                    <legend style={{ 
                        width: 'auto', 
                        padding: '0 0.5rem', 
                        fontSize: '1.1rem', 
                        fontWeight: '600',
                        marginBottom: 0,
                        border: 'none'
                    }}>
                        <span>Structured Address</span>
                        <Button
                            icon={<Search size={16} />}
                            text
                            size="small"
                            className="ml-2"
                            tooltip="View JSON"
                            onClick={this.onShowJsonModal}
                            style={{ padding: '0.25rem' }}
                        />
                    </legend>

                    <Row>
                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="department">Department</Label>
                                <InputText
                                    id="department"
                                    value={structuredAddress.department || ""}
                                    onChange={(e) => this.onFieldChange("department", e.target.value)}
                                    className="form-control"
                                    maxLength={70}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="subDepartment">Sub Department</Label>
                                <InputText
                                    id="subDepartment"
                                    value={structuredAddress.subDepartment || ""}
                                    onChange={(e) => this.onFieldChange("subDepartment", e.target.value)}
                                    className="form-control"
                                    maxLength={70}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="streetName">Street Name</Label>
                                <InputText
                                    id="streetName"
                                    value={structuredAddress.streetName || ""}
                                    onChange={(e) => this.onFieldChange("streetName", e.target.value)}
                                    className="form-control"
                                    maxLength={70}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="buildingNumber">Building Number</Label>
                                <InputText
                                    id="buildingNumber"
                                    value={structuredAddress.buildingNumber || ""}
                                    onChange={(e) => this.onFieldChange("buildingNumber", e.target.value)}
                                    className="form-control"
                                    maxLength={10}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="buildingName">Building Name</Label>
                                <InputText
                                    id="buildingName"
                                    value={structuredAddress.buildingName || ""}
                                    onChange={(e) => this.onFieldChange("buildingName", e.target.value)}
                                    className="form-control"
                                    maxLength={70}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="floor">Floor</Label>
                                <InputText
                                    id="floor"
                                    value={structuredAddress.floor || ""}
                                    onChange={(e) => this.onFieldChange("floor", e.target.value)}
                                    className="form-control"
                                    maxLength={10}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="postBox">PO Box</Label>
                                <InputText
                                    id="postBox"
                                    value={structuredAddress.postBox || ""}
                                    onChange={(e) => this.onFieldChange("postBox", e.target.value)}
                                    className="form-control"
                                    maxLength={12}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="room">Room</Label>
                                <InputText
                                    id="room"
                                    value={structuredAddress.room || ""}
                                    onChange={(e) => this.onFieldChange("room", e.target.value)}
                                    className="form-control"
                                    maxLength={10}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="postCode">Post Code</Label>
                                <InputText
                                    id="postCode"
                                    value={structuredAddress.postCode || ""}
                                    onChange={(e) => this.onFieldChange("postCode", e.target.value)}
                                    className="form-control"
                                    maxLength={16}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="townName">City / Town *</Label>
                                <InputText
                                    id="townName"
                                    value={structuredAddress.townName || ""}
                                    onChange={(e) => this.onFieldChange("townName", e.target.value)}
                                    className="form-control"
                                    maxLength={70}
                                    required
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="townLocName">Locality (Town Area)</Label>
                                <InputText
                                    id="townLocName"
                                    value={structuredAddress.townLocName || ""}
                                    onChange={(e) => this.onFieldChange("townLocName", e.target.value)}
                                    className="form-control"
                                    maxLength={70}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="districtName">District</Label>
                                <InputText
                                    id="districtName"
                                    value={structuredAddress.districtName || ""}
                                    onChange={(e) => this.onFieldChange("districtName", e.target.value)}
                                    className="form-control"
                                    maxLength={70}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="countrySubDiv">Province / Region</Label>
                                <InputText
                                    id="countrySubDiv"
                                    value={structuredAddress.countrySubDiv || ""}
                                    onChange={(e) => this.onFieldChange("countrySubDiv", e.target.value)}
                                    className="form-control"
                                    maxLength={70}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6} lg={4} xl={3} className="p-2.5">
                            <FormGroup>
                                <Label for="country">Country *</Label>
                                <Dropdown
                                    id="country"
                                    value={structuredAddress.country}
                                    options={this.state.countries}
                                    optionLabel="label"  // Display the full country name
                                    optionValue="value"  // Use the ISO-2 code as the actual value
                                    onChange={(e) => this.onFieldChange("country", e.value)}
                                    placeholder="Select Country"
                                    filter
                                    filterBy="label"
                                    className="w-100"
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <div className="flex justify-end px-5 py-4">
                        <Button
                            label="Save"
                            icon={this.state.isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            onClick={this.onSave}
                            disabled={this.state.isSaving}
                        />
                    </div>
                </fieldset>

                {this.renderJsonModal()}
            </Fragment>
        );
    }
}